/**
 * Sentry instrumentation for Web
 * This file initializes Sentry with Session Replay for the web client
 *
 * IMPORTANT: Import initSentry in main.tsx before any other modules
 */

import * as Sentry from '@sentry/browser';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:service:sentry');

let isInitialized = false;

/**
 * Initialize Sentry for web
 * Call this once in your app initialization (e.g., main.tsx)
 */
export function initSentry() {
  const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
  const isProduction = import.meta.env.PROD;
  const environment = import.meta.env.MODE || 'development';

  // Only initialize Sentry if DSN is provided
  if (!SENTRY_DSN) {
    logger.warn('⚠️ [SENTRY] DSN not provided, Sentry will not be initialized');
    return;
  }

  const productionSampleRate = 1.0; // 100% in production
  const developmentSampleRate = 1.0; // 100% in development for testing

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment,

      // Performance Monitoring
      tracesSampleRate: isProduction ? productionSampleRate : developmentSampleRate,

      // Session Replay sampling rates
      replaysSessionSampleRate: isProduction ? productionSampleRate : developmentSampleRate,
      replaysOnErrorSampleRate: 1.0, // Always capture on error

      // Integrations
      integrations: [
        // Session Replay integration for video-like reproductions
        Sentry.replayIntegration({
          // ── PHI ──────────────────────────────────────────────────────────
          // Replay ships a DOM recording to a third party at 100% sampling.
          // This is a healthcare product: patient names, dates of birth and
          // MRNs are three of HIPAA's eighteen identifiers, and they render on
          // ordinary screens here (task claim card, Real-Time Mode snapshot,
          // chat). Global masking is left OFF so replays stay useful for
          // layout and interaction debugging; anything that can carry PHI is
          // masked EXPLICITLY via the selectors below.
          //
          // Mark a new PHI surface with `data-phi` (or `.sentry-mask`) — the
          // attribute is the contract, so masking travels with the component
          // instead of living in a list someone has to remember to update.
          mask: ['[data-phi]', '.sentry-mask'],
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: false,

          // Network request/response capture for backend API debugging
          // Local origins ONLY. Capturing bodies for the deployed gateway meant
          // the raw /tasks and /tasks/claim-summary responses — patient name,
          // DOB and MRN — were recorded verbatim, which no amount of DOM
          // masking would have covered. Debug bodies against localhost.
          networkDetailAllowUrls: ['http://localhost', 'http://127.0.0.1'],

          // Capture basic headers for debugging
          networkRequestHeaders: ['Content-Type', 'Accept', 'User-Agent'],
          networkResponseHeaders: ['Content-Type', 'Content-Length'],

          // Enable capture of request and response bodies
          networkCaptureBodies: true,
        }),
      ],

      beforeSend: event => {
        // Remove sensitive headers from request data
        if (event.request?.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['Authorization'];
          delete event.request.headers['cookie'];
          delete event.request.headers['Cookie'];
          delete event.request.headers['x-api-key'];
          delete event.request.headers['X-API-Key'];
        }

        // Remove sensitive data from breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((breadcrumb: any) => {
            // Remove sensitive data from console breadcrumbs
            if (
              breadcrumb.category === 'console' &&
              breadcrumb.message &&
              typeof breadcrumb.message === 'string'
            ) {
              breadcrumb.message = breadcrumb.message.replace(/[a-zA-Z0-9_-]{20,}/g, '[REDACTED]');
            }

            // Remove sensitive headers from network breadcrumbs
            if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
              if (breadcrumb.data?.request_headers) {
                delete breadcrumb.data.request_headers.authorization;
                delete breadcrumb.data.request_headers.Authorization;
                delete breadcrumb.data.request_headers.cookie;
                delete breadcrumb.data.request_headers.Cookie;
              }
              if (breadcrumb.data?.response_headers) {
                delete breadcrumb.data.response_headers['set-cookie'];
                delete breadcrumb.data.response_headers['Set-Cookie'];
              }
            }
            return breadcrumb;
          });
        }

        // Add custom context
        if (event.contexts) {
          event.contexts.runtime = {
            name: 'web-browser',
            version: navigator.userAgent,
          };
        }

        return event;
      },

      beforeSendTransaction: transaction => {
        transaction.tags = {
          ...transaction.tags,
          platform: 'web',
        };
        return transaction;
      },
    });

    isInitialized = true;
    logger.info('✅ [SENTRY] Initialized for web with Session Replay (PHI surfaces masked)');
  } catch (error) {
    logger.error('❌ [SENTRY] Failed to initialize:', error);
  }
}

/**
 * Export Sentry for use in the app
 */
export { Sentry };

/**
 * Helper to check if Sentry is initialized
 */
export const isSentryInitialized = () => isInitialized;
